import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { banners } from "@/mocks/home";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const total = banners.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const active = banners[index];

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-primary-950 md:h-[600px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {banners.map((banner, i) => (
        <div
          key={banner.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={banner.image}
            alt={`${banner.title} 焦点图`}
            className="h-full w-full object-cover object-center"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>

      <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-4 md:px-6">
        <div key={active.id} className="max-w-2xl animate-riseIn">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-fill"></i>
            白虎热播推荐
          </span>
          <h1 className="mt-4 text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-2 font-label text-sm tracking-wide text-accent-300 md:text-base">
            {active.subtitle}
          </p>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {active.tagline}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.workId}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/movies"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览全部影片
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {banners.map((banner, i) => (
          <button
            key={banner.id}
            type="button"
            aria-label={`切换到 ${banner.title}`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-7 bg-accent-500" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-xl text-background-50 transition-colors hover:bg-primary-950/70 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-xl text-background-50 transition-colors hover:bg-primary-950/70 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>
    </section>
  );
}